import Repository from './Repository';
import { CreateRecipeCategory } from '@/models/Recipes/CreateRecipeCategory';
import { DeleteRecipeCategory } from '@/models/Recipes/DeleteRecipeCategory';
import ListRequest from '@/models/ListRequest';
import { CreateRecipe } from '@/models/Recipes/CreateRecipe';
import { UpdateRecipe } from '@/models/Recipes/UpdateRecipe';
import { SearchSubRecipes } from '@/models/Recipes/SearchSubRecipes';
import { SearchRecipes } from '@/models/Recipes/SearchRecipes';
import { RecipeCalculate } from '@/models/Recipes/RecipeCalculate';
import { Recipe } from '@/models/Recipes/Recipe';
import { RecipeCompositionMeasurement } from '@/models/Recipes/RecipeCompositionMeasurement';
import { ListResult } from './ResultTypes/ListResult';
import { MethodResult } from './ResultTypes/MethodResult';
import { AxiosResponse } from 'axios';
import {AminoAcidScoreDto} from "@/models/Recipes/AminoAcidScoreDto";
import {PrintRecipeDetails} from "@/models/Recipes/PrintRecipeDetails";
import { Dish } from '@/models/Dishes/Dish';
import { RecipeMetadata } from '@/models/Recipes/RecipeMetadata';
import {Nutritions} from "@/models/Recipes/Nutritions";


export const resource = '/recipe';

export default {
  createRecipeCategory(createRecipeCategory: CreateRecipeCategory) {
    return Repository.post(`${resource}/CreateRecipeCategory`, createRecipeCategory, { withCredentials: true });
  },
  listRecipeCategories() {
    return Repository.get(`${resource}/ListRecipeCategories`, { withCredentials: true });
  },
  deleteRecipeCategory(deleteRecipeCategory: DeleteRecipeCategory) {
    return Repository.post(`${resource}/DeleteRecipeCategory`, deleteRecipeCategory, { withCredentials: true });
  },
  listRecipes(listRequest: ListRequest) {
    return Repository.post(`${resource}/ListRecipes`, listRequest, { withCredentials: true });
  },
  listReducedRecipes(listRequest: ListRequest, isRecipe: Boolean = false) {
    return Repository.post(`${resource}/ListReducedRecipes`, { queryOptions: listRequest.queryOptions, isRecipe}, { withCredentials: true });
  },
  getRecipeDetails(id: number, limitNutrientsByCountry: boolean = false): Promise<AxiosResponse<Recipe>> {
    return Repository.get<Recipe>(
      `${resource}/${id}/Details?limitNutrientsByCountry=${limitNutrientsByCountry}`,
      { withCredentials: true }
    );
  },
  createRecipe(createRecipe: CreateRecipe): Promise<AxiosResponse<Recipe>> {
    return Repository.post(`${resource}/Create`, createRecipe, { withCredentials: true });
  },
  copyRecipe(recipeId: number, newName: string): Promise<AxiosResponse<Recipe>> {
    return Repository.post(`${resource}/${recipeId}/Copy`, { newName }, { withCredentials: true });
  },
  deleteRecipe(id: number) {
    return Repository.post(`${resource}/DeleteRecipe`, { recipeId: id }, { withCredentials: true });
  },
  updateRecipe(updateRecipe: UpdateRecipe) {
    return Repository.post(`${resource}/UpdateRecipe`, updateRecipe, { withCredentials: true });
  },
  searchSubRecipes(searchSubRecipes: SearchSubRecipes) {
    return Repository.post(`${resource}/SearchSubRecipes`, searchSubRecipes, { withCredentials: true });
  },
  searchRecipes(searchRecipes: SearchRecipes) {
    return Repository.post(`${resource}/SearchRecipes`, searchRecipes, { withCredentials: true });
  },
  renameCategory(updateCategory: any) {
    return Repository.post(`${resource}/RenameRecipeCategory`, updateCategory, { withCredentials: true });
  },
  moveRecipesToCategory(moveRecipesToCategory: any) {
    return Repository.post(`${resource}/MoveRecipesToRecipeCategory`, moveRecipesToCategory, { withCredentials: true });
  },
  filterRecipesNames(recipesSearchTerm: string) {
    return Repository.post(`${resource}/filterRecipesNames`, { recipeSearchTerm: recipesSearchTerm }, { withCredentials: true });
  },
  filterCombinedRecipeName(recipesSearchTerm: string){
    return Repository.post(`${resource}/filterCombinedRecipeNames`, { recipeSearchTerm: recipesSearchTerm }, { withCredentials: true });
  },
  calculate(recCalc: RecipeCalculate) {
    return Repository.post(`${resource}/CalculateRecipeNutritions`, recCalc, { withCredentials: true });
  },
  getWellnessCalories(type: string, id: number) {
    return Repository.get(`${resource}/Calories?type=${encodeURIComponent(type)}&id=${id}`, { withCredentials: true });
  },
  saveRecipeLabel(data: any) {
    return Repository.post(`${resource}/SaveRecipeLabel`, data, { withCredentials: true });
  },
  getRecipeLabel(recipeId: number) {
    return Repository.post(
      `${resource}/GetRecipeLabel`,
      { recipeId },
      { withCredentials: true }
    );
  },
  deleteRecipes(data: any) {
    return Repository.post(`${resource}/DeleteRecipes`, data, { withCredentials: true });
  },
  listRestaurantRecipesAsLookups(restaurantId: number) {
    return Repository.get(`${resource}/ListRestaurantRecipesAsLookups?restaurantId=${restaurantId}`, { withCredentials: true });
  },
  listCookingMethods() {
    return Repository.get(`${resource}/ListCookingMethods`, { withCredentials: true });
  },
  listFoodGroupClasses() {
    return Repository.get(`${resource}/ListFoodGroupClasses`, { withCredentials: true });
  },
  listFoodGroupSubClasses(data: any) {
    return Repository.post(`${resource}/ListFoodGroupSubClasses`, data, { withCredentials: true });
  },
  updateRecipeFoodClassifications(data: any) {
    return Repository.post(`${resource}/UpdateRecipeFoodClassifications`, data, { withCredentials: true });
  },
  updateRecipeServingSize(data: any) {
    return Repository.post(`${resource}/UpdateRecipeServingSize`, data, { withCredentials: true });
  },
  updateCaloriesCalculationMethod(data: any) {
    return Repository.post(`${resource}/UpdateCalculationMethods`, data, { withCredentials: true });
  },
  hasRecipeWithIngredientsFromSpecificDatabase(nutrientSource: any) {
    return Repository.post(`${resource}/HasRecipeWithIngredientsFromSpecificDatabase`, { nutrientSource: nutrientSource }, { withCredentials: true });
  },
  listRecipesThatUseIngredient(ingredientId: number) {
    return Repository.get(`${resource}/ListRecipesThatUseIngredient?IngredientId=${ingredientId}`, { withCredentials: true });
  },
  getRecipeCompositionMeasurements() {
    return Repository.get<ListResult<RecipeCompositionMeasurement>>(
      `${resource}/GetRecipeCompositionMeasurements`,
      { withCredentials: true }
    );
  },
  getAminoAcidScore(recipeId: number) {
    return Repository.get<MethodResult<AminoAcidScoreDto>>(
      `${resource}/GetAminoAcidScoreBreakdown?recipeId=${recipeId}`,
      { withCredentials: true }
    );
  },
  getPrintRecipeDetails(recipeId: number): Promise<AxiosResponse<PrintRecipeDetails>> {
    return Repository.get<PrintRecipeDetails>(
      `${resource}/PrintRecipeDetails?recipeId=${recipeId}`,
      { withCredentials: true }
    );
  },
  updateRecipeCountry(recipeMetadata: RecipeMetadata) {
    return Repository.patch<{recipeId:number, countryId:number, isSupplementLabel:boolean}, AxiosResponse<Nutritions>>(
      `${resource}/UpdateRecipeCountry`,
      recipeMetadata ,
      { withCredentials: true }
    );
  },
  UpdateMetaDataIsSupplementLabel(recipeId: number, isSupplementLabel: boolean) {
    return Repository.patch(
      `${resource}/UpdateMetaDataIsSupplementLabel?recipeId=${recipeId}`,
      { isSupplementLabel },
      { withCredentials: true }
    );
  },
  updateShowAllergenOnPrintout(recipeId: number, showAllergenOnPrintout: boolean) {
    return Repository.patch(
      `${resource}/UpdateShowAllergenOnPrintout?recipeId=${recipeId}`,
      { showAllergenOnPrintout },
      { withCredentials: true }
    );
  },
  updateRecipeRoundingRule(data: { ruleIndex: number; recipeId: number; countryId: number }) {
    return Repository.patch<typeof data, AxiosResponse<Recipe>>(
      `${resource}/UpdateRecipeRoundingRule`,
      data,
      { withCredentials: true }
    );
  },
  updateCombinedRecipeCountry(recipeMetadata: RecipeMetadata) {
    return Repository.patch<{recipeId:number, countryId:number, isSupplementLabel:boolean}, AxiosResponse<Dish>>(
      `${resource}/updateCombinedRecipeCountry`,
      recipeMetadata ,
      { withCredentials: true }
    );
  },
  updateRecipeReferenceAmount(recipeId:number, referenceAmount:number){
    return Repository.patch(`${resource}/UpdateRecipeReferenceAmount`, { recipeId: recipeId, referenceAmount: referenceAmount }, { withCredentials: true });
  },
  getProductTypesByCountry(countryId: number) {
    return Repository.get(`${resource}/GetProductTypesByCountry?countryId=${countryId}`, { withCredentials: true });
  },
  getRecipeMetaData(recipeId: number): Promise<AxiosResponse<RecipeMetadata>>{
    return Repository.get<RecipeMetadata>(`${resource}/GetRecipeMetaData?recipeId=${recipeId}`, { withCredentials: true });
  },
  updateRecipeImageData(recipeId: number, imageBase64Data: string) {
    return Repository.patch(
      `${resource}/UpdateRecipeImageData?recipeId=${recipeId}`,
      { imageBase64Data },
      { withCredentials: true }
    );
  },
};